import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, ExternalLink, Search, ChevronLeft, ChevronRight } from "lucide-react";
import {
  BRAND, LEAD_SOURCES, LEAD_SOURCE_LABEL, LEAD_STAGES, LEAD_STAGE_LABEL,
  statusOptionsFor, normalizeStage, type UnifiedLead, type LeadSource,
} from "@avestate/shared";
import { useInbox, usePatchLead } from "@/hooks/useInbox.js";
import { useRealtimeInquiries } from "@/hooks/useRealtimeInquiries.js";
import { Button } from "@/components/ui/button.js";
import { Badge } from "@/components/ui/badge.js";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer.js";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "@/components/ui/table.js";
import { StatusPill, statusLabel } from "@/components/signature/StatusPill.js";
import { RoleGate } from "@/components/RoleGate.js";
import { fmtDateTime, fmtEur } from "@/lib/format.js";
import { waLink } from "@/lib/whatsapp.js";

const PAGE_SIZE = 50;
const SOURCE_TONE: Record<LeadSource, "accent" | "ai" | "copper" | "system"> = {
  inquiry: "accent", booking: "ai", seller: "copper", valuation: "system",
};
const isWhatsApp = (l: UnifiedLead): boolean => (l.meta as { channel?: string }).channel === "whatsapp";

export default function Inbox(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const source = params.get("source") ?? "";
  const stage = params.get("stage") ?? "";
  const q = params.get("q") ?? "";
  const listing_id = params.get("listing_id") ?? "";
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [source, stage, q, listing_id]);

  const filters = useMemo(
    () => ({ source: source || undefined, stage: stage || undefined, q: q || undefined, listing_id: listing_id || undefined, page, page_size: PAGE_SIZE }),
    [source, stage, q, listing_id, page],
  );
  const { data, isLoading } = useInbox(filters);
  const patch = usePatchLead();
  const [open, setOpen] = useState<UnifiedLead | null>(null);
  useRealtimeInquiries();

  const items = data?.items ?? [];
  const counts = data?.counts;
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function setParam(key: string, value: string) {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value); else p.delete(key);
    setParams(p, { replace: true });
  }

  // Debounced search: type into local state, write the URL param after a pause.
  const [qInput, setQInput] = useState(q);
  useEffect(() => { setQInput(q); }, [q]);
  useEffect(() => {
    const t = setTimeout(() => { if (qInput !== q) setParam("q", qInput); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  async function setStatus(lead: UnifiedLead, next: string) {
    try {
      await patch.mutateAsync({ source: lead.source, id: lead.id, status: next });
      setOpen((o) => (o ? { ...o, status: next, stage: normalizeStage(o.source, next) } : o));
      toast.success(`Stato → ${statusLabel(next)}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="space-y-[22px]">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Inbox lead</h1>
          <p className="mt-[5px] text-[13.5px] text-ink-soft">Richieste, visite, vendi-casa e valutazioni in un unico flusso.</p>
        </div>
        <label className="flex h-9 items-center gap-2 rounded-[6px] border border-rule bg-surface-2 px-3">
          <Search className="size-4 text-ink-mute" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Cerca nome, email, telefono…"
            className="w-[200px] bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-mute"
            aria-label="Cerca lead"
          />
        </label>
      </header>

      {listing_id ? (
        <div className="flex items-center gap-2 text-[12.5px] text-ink-soft">
          Filtrato per immobile
          <button type="button" onClick={() => setParam("listing_id", "")} className="rounded-pill border border-rule bg-surface-2 px-2 py-0.5 text-ink hover:border-accent">rimuovi filtro</button>
        </div>
      ) : null}

      {/* stat strip — clickable stage filters */}
      <div className="grid grid-cols-2 overflow-hidden rounded-[5px] border border-rule bg-surface sm:grid-cols-4">
        {LEAD_STAGES.map((s) => (
          <button key={s} type="button" aria-pressed={stage === s} onClick={() => setParam("stage", stage === s ? "" : s)}
            className={`border-r border-rule-soft p-[16px_18px] text-left transition-colors ${stage === s ? "bg-accent/10" : "hover:bg-surface-2"}`}>
            <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{counts?.stage[s] ?? 0}</div>
            <div className="micro mt-[5px]">{LEAD_STAGE_LABEL[s]}</div>
          </button>
        ))}
        <div className="p-[16px_18px]">
          <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{(counts?.stage.nuovo ?? 0) + (counts?.stage.in_corso ?? 0) + (counts?.stage.chiuso ?? 0)}</div>
          <div className="micro mt-[5px]">Totale{data?.truncated ? "+" : ""}</div>
        </div>
      </div>

      {/* source filter chips */}
      <div className="flex flex-wrap gap-2">
        <Chip active={!source} onClick={() => setParam("source", "")}>Tutte</Chip>
        {LEAD_SOURCES.map((s) => (
          <Chip key={s} active={source === s} onClick={() => setParam("source", source === s ? "" : s)}>
            {LEAD_SOURCE_LABEL[s]} <span className="text-ink-mute">{counts?.source[s] ?? 0}</span>
          </Chip>
        ))}
      </div>

      <div className="overflow-x-auto rounded-[5px] border border-rule bg-surface">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-[110px]">Stato</TableHeader>
              <TableHeader className="w-[140px]">Tipo</TableHeader>
              <TableHeader>Contatto</TableHeader>
              <TableHeader>Oggetto</TableHeader>
              <TableHeader className="w-[160px]">Immobile</TableHeader>
              <TableHeader className="w-[150px] text-right">Ricevuto</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => (
              <TableRow key={`${row.source}-${row.id}`} onClick={() => setOpen(row)} className="cursor-pointer">
                <TableCell><StatusPill status={row.status} /></TableCell>
                <TableCell className="space-x-1 whitespace-nowrap">
                  <Badge tone={SOURCE_TONE[row.source]}>{LEAD_SOURCE_LABEL[row.source]}</Badge>
                  {isWhatsApp(row) ? <Badge tone="success">WA</Badge> : null}
                </TableCell>
                <TableCell>
                  <p className="text-body-strong text-ink">{row.name || row.email || "—"}</p>
                  <p className="text-meta text-ink-soft">{isWhatsApp(row) ? row.phone : (row.email || row.phone || "")}</p>
                </TableCell>
                <TableCell className="max-w-[320px] truncate text-[13px] text-ink-soft">{row.subject}</TableCell>
                <TableCell>
                  {row.listing_id ? (
                    <Link to={`/listings/${row.listing_id}`} onClick={(e) => e.stopPropagation()} className="text-meta text-accent hover:underline">
                      {row.listing_title || row.listing_slug || "apri"}
                    </Link>
                  ) : <span className="text-ink-mute">—</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-meta text-ink-mute">{fmtDateTime(row.created_at)}</TableCell>
              </TableRow>
            ))}
            {!isLoading && items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="py-12 text-center text-ink-mute">Nessun lead.</TableCell></TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      {total > 0 ? (
        <div className="flex items-center justify-between text-[12.5px] text-ink-soft">
          <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} di {total}{data?.truncated ? " (recenti)" : ""}</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Pagina precedente"><ChevronLeft className="size-4" /></Button>
            <span className="px-2 font-mono">{page}/{lastPage}</span>
            <Button size="sm" variant="outline" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} aria-label="Pagina successiva"><ChevronRight className="size-4" /></Button>
          </div>
        </div>
      ) : null}

      <Drawer open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DrawerContent>{open ? <LeadDetail lead={open} onStatus={setStatus} /> : null}</DrawerContent>
      </Drawer>
    </div>
  );
}

function LeadDetail({ lead, onStatus }: { lead: UnifiedLead; onStatus: (l: UnifiedLead, s: string) => void }): JSX.Element {
  const meta = lead.meta as Record<string, unknown>;
  const whatsapp = isWhatsApp(lead);
  const showEmail = !!lead.email && !lead.email.endsWith("@whatsapp.lead");
  const waText = `Ciao ${lead.name ?? ""}, ti scriviamo da ${BRAND.name} riguardo "${lead.subject}".`.trim();
  const wa = lead.phone ? waLink(lead.phone, waText) : null;

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-rule p-6 pr-12">
        <DrawerTitle className="text-h2 font-display text-ink">{lead.name || lead.email || lead.subject}</DrawerTitle>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge tone="neutral">{LEAD_SOURCE_LABEL[lead.source]}</Badge>
          {whatsapp ? <Badge tone="success">WhatsApp</Badge> : null}
          <StatusPill status={lead.status} />
          <span className="font-mono text-meta uppercase text-ink-mute">{fmtDateTime(lead.created_at)}</span>
        </div>
      </header>

      <div className="flex-1 space-y-5 overflow-y-auto p-6">
        <section className="space-y-2">
          <h3 className="font-mono text-meta uppercase tracking-wider text-ink-mute">Contatti</h3>
          {showEmail ? (
            <div className="flex items-center gap-2 text-body"><Mail className="size-4 text-ink-mute" /><a href={`mailto:${lead.email}`} className="text-accent hover:underline">{lead.email}</a></div>
          ) : null}
          {lead.phone ? (
            <div className="flex items-center gap-2 text-body"><Phone className="size-4 text-ink-mute" /><a href={`tel:${lead.phone}`} className="text-accent hover:underline">{lead.phone}</a></div>
          ) : null}
          {!showEmail && !lead.phone ? <p className="text-meta text-ink-mute">Nessun contatto (lead anonimo).</p> : null}
        </section>

        {lead.message ? (
          <section className="space-y-2">
            <h3 className="font-mono text-meta uppercase tracking-wider text-ink-mute">Messaggio</h3>
            <p className="whitespace-pre-wrap rounded border border-rule bg-surface-2 p-3 text-body text-ink">{lead.message}</p>
          </section>
        ) : null}

        {lead.source === "valuation" ? (
          <section className="space-y-1">
            <h3 className="font-mono text-meta uppercase tracking-wider text-ink-mute">Valutazione</h3>
            <p className="text-body text-ink">Atteso <span className="font-semibold text-accent">{fmtEur(meta.output_expected as number)}</span> <span className="text-ink-mute">({fmtEur(meta.output_low as number)} – {fmtEur(meta.output_high as number)})</span></p>
          </section>
        ) : null}
        {lead.source === "seller" ? (
          <section className="space-y-1">
            <h3 className="font-mono text-meta uppercase tracking-wider text-ink-mute">Immobile da vendere</h3>
            <p className="text-body text-ink">{String(meta.address ?? "")}{meta.city ? `, ${meta.city}` : ""}</p>
            <p className="text-meta text-ink-soft">{String(meta.property_type ?? "")}{meta.mq ? ` · ${meta.mq} mq` : ""}</p>
          </section>
        ) : null}
        {lead.source === "booking" && meta.preferred_date ? (
          <section className="space-y-1">
            <h3 className="font-mono text-meta uppercase tracking-wider text-ink-mute">Visita richiesta</h3>
            <p className="text-body text-ink">{String(meta.preferred_date)}{meta.preferred_slot ? ` · ${meta.preferred_slot}` : ""}</p>
          </section>
        ) : null}

        {lead.listing_id ? (
          <Link to={`/listings/${lead.listing_id}`} className="inline-flex items-center gap-1 font-mono text-meta uppercase tracking-wider text-accent hover:underline">
            <ExternalLink className="size-3.5" /> {lead.listing_title || lead.listing_slug || "apri immobile"}
          </Link>
        ) : null}
      </div>

      <RoleGate min="editor">
        <footer className="space-y-3 border-t border-rule bg-surface-2 p-4">
          <div className="flex flex-wrap gap-2">
            {statusOptionsFor(lead.source).map((s) => (
              <Button key={s} size="sm" variant={lead.status === s ? "primary" : "outline"} onClick={() => onStatus(lead, s)}>{statusLabel(s)}</Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {wa ? (
              <Button asChild size="sm" variant="ghost"><a href={wa} target="_blank" rel="noopener noreferrer"><MessageCircle className="size-3.5" /> WhatsApp</a></Button>
            ) : null}
            {showEmail ? (
              <Button asChild size="sm" variant="ghost"><a href={`mailto:${lead.email}?subject=${encodeURIComponent(`Re: la tua richiesta — ${BRAND.name}`)}`}><Mail className="size-3.5" /> Email</a></Button>
            ) : null}
          </div>
        </footer>
      </RoleGate>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button type="button" aria-pressed={active} onClick={onClick}
      className={`rounded-pill border px-3 py-1 text-[12.5px] transition-colors ${active ? "border-accent bg-accent/10 text-ink" : "border-rule bg-surface-2 text-ink-soft hover:border-accent hover:text-ink"}`}>
      {children}
    </button>
  );
}
